import React, { useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
  CircularProgress, Stack, Alert, IconButton, Tooltip, Chip, Box,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { createDraftMessage } from '../../utils/graphMailSearch';
import { startActivity } from '../../utils/activityTracker';

// Begeleidende e-mail bij een offerte. Met offertenummer wordt de DEFINITIEVE
// versie uit Gripp geanalyseerd (dus inclusief je handmatige aanpassingen daar).
// Fijnslijpen kan via de mini-chat; daarna copy-paste of direct als Outlook-
// concept klaarzetten (contactpersoon in Aan, offerte-PDF als bijlage).
export default function OfferteEmailButton({ subject, description, companyName, contactName, contactEmail, offerNumber, source, senderName, variant = 'outlined', sx }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [mailSubject, setMailSubject] = useState('');
  const [body, setBody] = useState('');
  const [copied, setCopied] = useState('');
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null); // { webLink } | { error }

  const generate = async () => {
    setLoading(true); setErr(null); setChatMsgs([]); setDraftResult(null);
    const act = startActivity('Begeleidende e-mail genereren', { path: '/offertes' });
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, description, companyName, contactName, offerNumber, source, senderName }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Mail genereren mislukt');
      setMailSubject(d.subject || ''); setBody(d.body || '');
      act.done('E-mail staat klaar');
    } catch (e) { setErr(e.message); act.fail(e.message); } finally { setLoading(false); }
  };

  const openDialog = () => { setOpen(true); setCopied(''); if (!body) generate(); };
  const copy = (text, which) => { try { navigator.clipboard.writeText(text); setCopied(which); setTimeout(() => setCopied(''), 1500); } catch { /* ignore */ } };

  // 💬 Fijnslijpen: korte chat die telkens de volledige bijgewerkte mail teruggeeft.
  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const messages = [...chatMsgs, { role: 'user', content: text }];
    setChatMsgs(messages); setChatInput(''); setChatBusy(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/offertes/email-chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages, subject: mailSubject, body,
          context: `Offerte ${offerNumber || ''} — ${subject || ''} — klant ${companyName || ''}${contactName ? `, contactpersoon ${contactName}` : ''}`,
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Aanpassen mislukt');
      setMailSubject(d.subject || mailSubject);
      setBody(d.body || body);
      setChatMsgs((prev) => [...prev, { role: 'assistant', content: d.reply || 'Aangepast ✔' }]);
    } catch (e) {
      setChatMsgs((prev) => [...prev, { role: 'assistant', content: `⚠️ ${e.message}` }]);
    } finally { setChatBusy(false); }
  };

  // 📤 Als Outlook-concept klaarzetten: contactpersoon in Aan + offerte-PDF als bijlage.
  const makeOutlookDraft = async () => {
    if (drafting || !body) return;
    setDrafting(true); setDraftResult(null);
    const act = startActivity('Outlook-concept klaarzetten', { path: '/offertes' });
    try {
      const attachments = [];
      if (offerNumber) {
        try {
          const r = await authFetch(`${BACKEND_URL}/Api/offertes/offer-pdf?number=${encodeURIComponent(offerNumber)}`);
          const d = await r.json();
          if (r.ok && d.base64) attachments.push({ name: d.filename, base64: d.base64 });
        } catch { /* bijlage is nice-to-have — concept zonder PDF is ook bruikbaar */ }
      }
      const { webLink } = await createDraftMessage({
        subject: mailSubject, body,
        to: contactEmail ? [contactEmail] : [],
        attachments,
      });
      setDraftResult({ webLink, withPdf: attachments.length > 0, to: contactEmail || null });
      act.done('Concept staat in Outlook');
    } catch (e) { setDraftResult({ error: e.message }); act.fail(e.message); }
    finally { setDrafting(false); }
  };

  return (
    <>
      <Button variant={variant} startIcon={<EmailOutlinedIcon fontSize="small" />} onClick={openDialog}
        sx={{ textTransform: 'none', fontWeight: 700, ...sx }}>
        Begeleidende e-mail
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          <EmailOutlinedIcon sx={{ color: '#4f46e5' }} />
          <Typography variant="h6" component="span">Begeleidende e-mail</Typography>
          {offerNumber && (
            <Tooltip title="De mail wordt gebaseerd op de offerte zoals die NU in Gripp staat — inclusief je handmatige aanpassingen daar.">
              <Chip size="small" label={`op basis van Gripp-offerte ${offerNumber}`} sx={{ bgcolor: '#eef2ff', color: '#4338ca', fontWeight: 700 }} />
            </Tooltip>
          )}
          <Tooltip title="Opnieuw genereren (haalt de actuele offerte opnieuw uit Gripp)">
            <span><Button size="small" onClick={generate} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />} sx={{ ml: 1, textTransform: 'none' }}>Opnieuw</Button></span>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          {loading && !body ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {offerNumber ? 'Definitieve offerte uit Gripp analyseren en ' : ''}e-mail schrijven in jullie tone-of-voice…
              </Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <TextField fullWidth size="small" label="Onderwerp" value={mailSubject} onChange={(e) => setMailSubject(e.target.value)} />
                <Tooltip title={copied === 'subject' ? 'Gekopieerd!' : 'Kopieer onderwerp'}>
                  <IconButton onClick={() => copy(mailSubject, 'subject')}><ContentCopyIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
              <TextField fullWidth multiline minRows={9} size="small" label="E-mailtekst" value={body} onChange={(e) => setBody(e.target.value)} />

              {/* Mini-chat: fijnslijpen zonder de dialog te verlaten */}
              {chatMsgs.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1.5, maxHeight: 160, overflowY: 'auto' }}>
                  {chatMsgs.map((m, i) => (
                    <Typography key={i} variant="caption" sx={{
                      alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                      bgcolor: m.role === 'user' ? '#eef2ff' : '#f1f5f9',
                      px: 1.25, py: 0.5, borderRadius: 1.5, maxWidth: '85%',
                    }}>
                      {m.content}
                    </Typography>
                  ))}
                </Stack>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="flex-end">
                <TextField fullWidth size="small" multiline maxRows={3}
                  placeholder="Fijnslijpen… (bv. 'korter en formeler', 'noem de deadline van 1 september')"
                  value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} />
                <Button variant="outlined" onClick={sendChat} disabled={chatBusy || !chatInput.trim()} sx={{ minWidth: 44, px: 1 }}>
                  {chatBusy ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                </Button>
              </Stack>

              {draftResult?.webLink && (
                <Alert severity="success" sx={{ mt: 1.5 }}
                  action={<Button size="small" color="inherit" href={draftResult.webLink} target="_blank" rel="noopener">Open concept</Button>}>
                  Concept staat in je Outlook-Concepten{draftResult.to ? ` — aan ${draftResult.to}` : ''}{draftResult.withPdf ? ', mét offerte-PDF als bijlage' : ' (PDF-bijlage kon niet worden opgehaald)'}.
                </Alert>
              )}
              {draftResult?.error && <Alert severity="error" sx={{ mt: 1.5 }}>Concept aanmaken mislukt: {draftResult.error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Tooltip title={contactEmail ? `Concept in Outlook met ${contactEmail} in Aan${offerNumber ? ' + offerte-PDF als bijlage' : ''}` : 'Geen e-mailadres van de contactpersoon bekend — concept komt zonder ontvanger'}>
              <span>
                <Button variant="contained" startIcon={drafting ? <CircularProgress size={14} color="inherit" /> : <DraftsIcon fontSize="small" />}
                  disabled={!body || drafting} onClick={makeOutlookDraft}
                  sx={{ bgcolor: '#059669', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#047857' } }}>
                  {drafting ? 'Concept aanmaken…' : 'Zet klaar in Outlook'}
                </Button>
              </span>
            </Tooltip>
          </Box>
          <Button onClick={() => copy(`Onderwerp: ${mailSubject}\n\n${body}`, 'all')} disabled={!body}
            sx={{ textTransform: 'none' }}>Kopieer alles</Button>
          <Button variant="contained" startIcon={<ContentCopyIcon fontSize="small" />} disabled={!body}
            onClick={() => copy(body, 'body')} sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#4338ca' } }}>
            {copied === 'body' ? 'Gekopieerd!' : 'Kopieer e-mailtekst'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
